/**
 * Utility functions for generating anonymous user profiles
 * Used for anonymous commenting feature
 */

const adjectives = [
    "다정한",
    "활발한",
    "조용한",
    "용감한",
    "귀여운",
    "차분한",
    "명랑한",
    "씩씩한",
    "온화한",
    "당당한",
    "재빠른",
    "느긋한",
    "똑똑한",
    "사랑스러운",
    "화려한",
    "신비한",
    "순수한",
    "행복한",
    "자유로운",
    "열정적인",
    "상냥한",
    "튼튼한",
    "영리한",
    "장난스러운",
    "포근한",
    "밝은",
    "성실한",
    "친절한",
    "듬직한"
];

const animals = [
    "돌고래",
    "호랑이",
    "토끼",
    "여우",
    "판다",
    "코알라",
    "펭귄",
    "고양이",
    "강아지",
    "사슴",
    "다람쥐",
    "햄스터",
    "수달",
    "부엉이",
    "앵무새",
    "고래",
    "거북이",
    "캥거루",
    "알파카",
    "라쿤",
    "기린",
    "코끼리",
    "사자",
    "곰",
    "늑대",
    "치타",
    "표범",
    "하마",
    "얼룩말",
    "원숭이"
];

/**
 * Generates a random anonymous nickname in the format "AdjectiveAnimal"
 * Example: "다정한돌고래", "활발한호랑이", "용감한토끼"
 */
export const generateAnonymousNickname = (): string => {
    const randomAdjective =
        adjectives[Math.floor(Math.random() * adjectives.length)];
    const randomAnimal = animals[Math.floor(Math.random() * animals.length)];
    return `${randomAdjective}${randomAnimal}`;
};

/**
 * Generates a random seed for boring-avatars
 * This seed is used to generate a consistent avatar for the profile
 */
export const generateAvatarSeed = (): string => {
    return (
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15)
    );
};

/**
 * Generates a complete anonymous profile with nickname and avatar seed
 */
export interface AnonymousProfile {
    nickname: string;
    avatarSeed: string;
}

export const generateAnonymousProfile = (): AnonymousProfile => {
    return {
        nickname: generateAnonymousNickname(),
        avatarSeed: generateAvatarSeed()
    };
};

/**
 * Storage keys for anonymous profile in localStorage
 */
export const ANONYMOUS_PROFILE_STORAGE_KEY = "mix_anonymous_profile";
export const ANONYMOUS_MODE_STORAGE_KEY = "mix_anonymous_mode_enabled";

/**
 * Saves anonymous profile to localStorage
 */
export const saveAnonymousProfile = (profile: AnonymousProfile): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem(
        ANONYMOUS_PROFILE_STORAGE_KEY,
        JSON.stringify(profile)
    );
};

/**
 * Retrieves anonymous profile from localStorage
 */
export const getAnonymousProfile = (): AnonymousProfile | null => {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem(ANONYMOUS_PROFILE_STORAGE_KEY);
    if (!stored) return null;
    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
};

/**
 * Saves anonymous mode enabled state
 */
export const setAnonymousModeEnabled = (enabled: boolean): void => {
    if (typeof window === "undefined") return;
    localStorage.setItem(ANONYMOUS_MODE_STORAGE_KEY, JSON.stringify(enabled));
};

/**
 * Gets anonymous mode enabled state
 */
export const getAnonymousModeEnabled = (): boolean | null => {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem(ANONYMOUS_MODE_STORAGE_KEY);
    if (!stored) return null; // null 반환하여 설정된 적 없음을 표시
    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
};

/**
 * Clears anonymous profile and mode from localStorage
 */
export const clearAnonymousProfile = (): void => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(ANONYMOUS_PROFILE_STORAGE_KEY);
    localStorage.removeItem(ANONYMOUS_MODE_STORAGE_KEY);
};

/**
 * Storage key for pending comments (per vote card)
 */
export const PENDING_COMMENT_STORAGE_KEY = "mix_pending_comments";

/**
 * Saves pending comment for a specific vote card
 */
export const savePendingComment = (voteId: string, text: string): void => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem(PENDING_COMMENT_STORAGE_KEY);
    let comments: Record<string, string> = {};

    if (stored) {
        try {
            comments = JSON.parse(stored);
        } catch {
            comments = {};
        }
    }

    comments[voteId] = text;
    localStorage.setItem(PENDING_COMMENT_STORAGE_KEY, JSON.stringify(comments));
};

/**
 * Gets pending comment for a specific vote card
 */
export const getPendingComment = (voteId: string): string | null => {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem(PENDING_COMMENT_STORAGE_KEY);

    if (!stored) return null;

    try {
        const comments = JSON.parse(stored);
        return comments[voteId] || null;
    } catch {
        return null;
    }
};

/**
 * Clears pending comment for a specific vote card
 */
export const clearPendingComment = (voteId: string): void => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem(PENDING_COMMENT_STORAGE_KEY);

    if (!stored) return;

    try {
        const comments = JSON.parse(stored);
        delete comments[voteId];
        localStorage.setItem(
            PENDING_COMMENT_STORAGE_KEY,
            JSON.stringify(comments)
        );
    } catch {
        // ignore
    }
};
